import logger from '../module/logger.js'
import cookie from '../module/cookie.js'

new (class {
  constructor() {
    logger.debug('[DataLayer] Initialized')

    window.addEventListener('cart.viewed', this.handleCartViewed)
    window.addEventListener('cart.line.added', this.handleCartLineAdded)
    window.addEventListener('cart.line.removed', this.handleCartLineRemoved)

    window.addEventListener('checkout.started', this.handleCheckoutStarted)
    window.addEventListener('checkout.email', this.handleCheckoutStep)
    window.addEventListener('checkout.login', this.handleCheckoutStep)
    window.addEventListener('checkout.details', this.handleCheckoutStep)
    window.addEventListener('checkout.shipping', this.handleCheckoutStep)
    window.addEventListener('checkout.payment', this.handleCheckoutPayment)
    window.addEventListener('checkout.payment.submitted', this.handleCheckoutPaymentSubmitted)

    window.addEventListener('product.detail.ready', this.handleProductDetailReady)
    window.addEventListener('product.configuration.updated', this.handleProductConfigurationUpdated)
    window.addEventListener('product.selected', this.handleProductSelected)
    window.addEventListener('product.list.viewed', this.handleProductListViewed)

    const productCalculatorEvents = ['available', 'opened', 'calculated', 'product_choices', 'add_to_cart']
    productCalculatorEvents.forEach((action) => {
      window.addEventListener(`product.calculator.${action}`, this.handleProductCalculator)
    })

    window.addEventListener('floor.inquiry.submitted', this.handleFloorInquirySubmitted)
    window.addEventListener('business.inquiry.submitted', this.handleBusinessInquirySubmitted)
    window.addEventListener('roof.landing.viewed', this.handleRoofLandingViewed)
    window.addEventListener('roof.cta.clicked', this.handleRoofCtaClicked)
    window.addEventListener('roof.form.started', this.handleRoofFormStarted)
    window.addEventListener('roof.form.step.completed', this.handleRoofFormStepCompleted)
    window.addEventListener('roof.form.error', this.handleRoofFormError)
    window.addEventListener('roof.lead.created', this.handleRoofLeadCreated)
    window.addEventListener('roof.contact.clicked', this.handleRoofContactClicked)
    window.addEventListener('contact.interaction', this.handleContactInteraction)

    document.addEventListener('click', this.handleStaticProductSelected)

    this.initStaticProductLists()
  }

  handleCartViewed = () => {
    const ecommerce = this.getEcommerceFromSummary()

    if (!ecommerce) {
      return
    }

    this.pushEcommerce('view_cart', ecommerce)
  }

  handleCartLineAdded = (event) => {
    const line = event.detail?.line

    if (!line) {
      return
    }

    this.push({
      event: 'squeezely.addToCart',
      SqueezelyAddToCart: {
        custom_cart_hash: cookie.get('cart'),
        products: [
          {
            id: line.productSku,
            name: line.name,
            price: line.price,
            quantity: line.quantity,
          },
        ],
      },
    })

    this.push({
      line_items: [
        {
          product_id: line.ga4ItemId,
          product_name: line.name,
          product_brand: line.brand,
          product_category: line.category,
          product_price: line.price,
          product_quantity: line.quantity,
        },
      ],
    })

    this.pushEcommerce('add_to_cart', {
      currency: 'EUR',
      value: this.value(line.price, line.quantity),
      items: [
        {
          item_id: line.ga4ItemId,
          item_name: line.name,
          currency: 'EUR',
          item_brand: line.brand,
          ...this.parseItemCategories(line.ga4Categories),
          item_variant: line.variant,
          price: line.price,
          quantity: line.quantity,
        },
      ],
    })
  }

  handleCartLineRemoved = (event) => {
    const line = event.target

    if (!(line instanceof HTMLElement) || !line.matches('[data-cart-item]')) {
      return
    }

    const price = Number.parseFloat(line.dataset.price)
    const quantity = Number.parseInt(line.dataset.quantity, 10)

    const item = {
      item_id: line.dataset.variantId,
      item_name: line.dataset.name,
      currency: 'EUR',
      item_brand: line.dataset.brand,
      ...this.parseItemCategories(line.dataset.ga4Categories),
      price,
      quantity,
    }

    if (line.dataset.variant) {
      item.item_variant = line.dataset.variant
    }

    const discount = Number.parseFloat(line.dataset.discount)

    if (discount > 0) {
      item.discount = discount
    }

    this.pushEcommerce('remove_from_cart', {
      currency: 'EUR',
      value: this.value(price, quantity),
      items: [item],
    })
  }

  handleCheckoutStarted = () => {
    const ecommerce = this.getEcommerceFromSummary()

    if (!ecommerce) {
      return
    }

    this.pushEcommerce('checkout.started', ecommerce)
  }

  handleCheckoutStep = (event) => {
    this.push({
      event: event.type,
    })
  }

  handleCheckoutPayment = () => {
    const ecommerce = this.getEcommerceFromSummary({ includeShippingTier: true })

    if (!ecommerce) {
      return
    }

    this.pushEcommerce('checkout.payment', ecommerce)
  }

  handleCheckoutPaymentSubmitted = (event) => {
    const paymentType = event.detail?.paymentType

    if (!paymentType) {
      return
    }

    const ecommerce = this.getEcommerceFromSummary()

    if (!ecommerce) {
      return
    }

    this.pushEcommerce('checkout.payment.submitted', {
      ...ecommerce,
      payment_type: paymentType,
    })
  }

  handleProductDetailReady = (event) => {
    if (event.detail?.variantSelected) {
      return
    }

    const item = this.getProductDetailItem()

    if (!item?.item_id || !Number.isFinite(item.price)) {
      return
    }

    this.pushProductRemarketing(item.item_id, item.price)
  }

  /**
   * A changed option is the same product view, so it is no new view_item, which would count the view again in GA4 and
   * the ad pixels. The chosen variant goes to select_variant instead, and to dynamic remarketing.
   */
  handleProductConfigurationUpdated = (event) => {
    const sku = event.detail?.sku
    const price = Number.parseFloat(event.detail?.price)
    const quantity = event.detail?.quantity
    const item = this.getProductDetailItem()

    if (!sku || !Number.isFinite(price) || !Number.isInteger(quantity) || !item) {
      return
    }

    this.pushProductRemarketing(sku, price)
    this.pushEcommerce('select_variant', {
      currency: 'EUR',
      value: this.value(price, quantity),
      items: [
        {
          item_id: sku,
          item_name: item.item_name,
          item_brand: item.item_brand,
          ...this.pickItemCategories(item),
          price,
          quantity,
        },
      ],
    })
  }

  handleProductSelected = (event) => {
    const product = event.detail?.product

    if (!product?.list || (!product.sku && !product.name)) {
      return
    }

    const item = {
      item_id: product.sku,
      item_name: product.name,
      item_brand: product.brand,
      ...product.categories,
    }

    if (Number.isInteger(product.index)) {
      item.index = product.index
    }

    this.pushEcommerce('select_item', {
      item_list_name: product.list,
      items: [item],
    })
  }

  handleProductListViewed = (event) => {
    const detail = event.detail

    if (!detail || detail.state === 'filtered') {
      return
    }

    this.pushProductList(detail.list, detail.products, {
      page: detail.page,
    })
  }

  handleProductCalculator = (event) => {
    if (!event.detail) {
      return
    }

    this.push({
      event: event.type,
      calculator: event.detail,
    })
  }

  handleFloorInquirySubmitted = (event) => {
    const floorType = event.detail?.floorType
    const surface = event.detail?.surface

    if (!floorType || !surface) {
      return
    }

    this.pushEnhancedConversionData(event.detail?.enhancedConversionData)
    this.push({
      event: 'floorInquiry',
      inquiry: {
        type: floorType,
        surface,
      },
    })
  }

  handleBusinessInquirySubmitted = (event) => {
    this.pushEnhancedConversionData(event.detail?.enhancedConversionData)
    this.push({
      event: 'generate_lead',
      lead_type: 'business',
      page_type: 'business_landing',
      source: 'business_form',
    })
  }

  handleRoofLandingViewed = (event) => {
    this.push({
      event: 'roof_landing_view',
      lead_type: event.detail?.leadType ?? 'roof_renovation',
      page_variant: event.detail?.pageVariant ?? 'renovation',
      schema_version: event.detail?.schemaVersion ?? 1,
    })
  }

  handleRoofCtaClicked = (event) => {
    this.push({
      event: 'roof_cta_click',
      lead_type: event.detail?.leadType ?? 'roof_renovation',
      position: event.detail?.position,
      schema_version: event.detail?.schemaVersion ?? 1,
    })
  }

  handleRoofFormStarted = (event) => {
    this.push({
      event: 'roof_form_start',
      lead_type: event.detail?.leadType ?? 'roof_renovation',
      form_id: event.detail?.formId ?? 'roof_quote',
      schema_version: event.detail?.schemaVersion ?? 1,
    })
  }

  handleRoofFormStepCompleted = (event) => {
    const data = {
      event: 'roof_form_step_complete',
      lead_type: event.detail?.leadType ?? 'roof_renovation',
      form_id: event.detail?.formId ?? 'roof_quote',
      step: event.detail?.step,
      schema_version: event.detail?.schemaVersion ?? 1,
    }

    if (event.detail?.audience) {
      data.audience = event.detail.audience
    }

    if (event.detail?.roofAreaBucket) {
      data.roof_area_bucket = event.detail.roofAreaBucket
    }

    this.push(data)
  }

  handleRoofFormError = (event) => {
    this.push({
      event: 'roof_form_error',
      lead_type: event.detail?.leadType ?? 'roof_renovation',
      form_id: event.detail?.formId ?? 'roof_quote',
      schema_version: event.detail?.schemaVersion ?? 1,
    })
  }

  handleRoofLeadCreated = (event) => {
    this.push({
      event: 'generate_lead',
      lead_type: event.detail?.leadType ?? 'roof_renovation',
      page_type: 'roof_renovation_landing',
      source: 'roof_form',
      schema_version: event.detail?.schemaVersion ?? 1,
    })
  }

  handleRoofContactClicked = (event) => {
    this.push({
      event: 'roof_contact_click',
      lead_type: event.detail?.leadType ?? 'roof_renovation',
      method: event.detail?.method,
      position: event.detail?.position,
      schema_version: event.detail?.schemaVersion ?? 1,
    })
  }

  handleContactInteraction = (event) => {
    const data = event.detail

    if (!data?.event || !data.event.startsWith('contact_')) {
      return
    }

    this.push(data)
  }

  /** The item of the product page's view_item push. */
  getProductDetailItem = () => {
    for (let index = window.dataLayer.length - 1; index >= 0; index -= 1) {
      const data = window.dataLayer[index]

      if (data?.event === 'view_item' && data.ecommerce?.items?.[0]) {
        return data.ecommerce.items[0]
      }
    }

    return null
  }

  pushProductRemarketing = (sku, price) => {
    this.push({
      event: 'ads_remarketing',
      google_tag_params: {
        ecomm_prodid: sku,
        ecomm_pagetype: 'product',
        ecomm_totalvalue: price,
      },
    })
  }

  getEcommerceFromSummary = ({ includeShippingTier = false } = {}) => {
    const summary = document.querySelector('[data-ecommerce-summary]')

    if (!(summary instanceof HTMLElement)) {
      return null
    }

    const value = Number.parseFloat(summary.dataset.ecommerceValue)

    let items

    try {
      items = JSON.parse(summary.dataset.ecommerceItems ?? '[]')
    } catch (error) {
      logger.error('[DataLayer] Failed to parse ecommerce items', error)

      return null
    }

    if (!Number.isFinite(value) || !Array.isArray(items) || items.length === 0) {
      return null
    }

    const ecommerce = {
      currency: 'EUR',
      value,
      items,
    }

    if (summary.dataset.ecommerceCoupon) {
      ecommerce.coupon = summary.dataset.ecommerceCoupon
    }

    if (includeShippingTier && summary.dataset.ecommerceShippingTier) {
      ecommerce.shipping_tier = summary.dataset.ecommerceShippingTier
    }

    return ecommerce
  }

  initStaticProductLists = () => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.observeStaticProductLists, { once: true })

      return
    }

    this.observeStaticProductLists()
  }

  observeStaticProductLists = () => {
    const lists = document.querySelectorAll('[data-product-list]')

    if (lists.length === 0) {
      return
    }

    if (!('IntersectionObserver' in window)) {
      lists.forEach(this.trackStaticProductList)

      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return
          }

          this.trackStaticProductList(entry.target)
          observer.unobserve(entry.target)
        })
      },
      {
        threshold: 0.1,
      },
    )

    lists.forEach((list) => observer.observe(list))
  }

  trackStaticProductList = (list) => {
    if (!(list instanceof HTMLElement)) {
      return
    }

    const products = Array.from(list.querySelectorAll('[data-product-card]'))
      .map((card, index) => {
        if (!(card instanceof HTMLElement)) {
          return null
        }

        return this.getProductCardData(card, index)
      })
      .filter(Boolean)

    this.pushProductList(list.dataset.productList, products)
  }

  handleStaticProductSelected = (event) => {
    const target = event.target

    if (!(target instanceof Element)) {
      return
    }

    const link = target.closest('[data-product-card-link]')

    if (!(link instanceof HTMLElement)) {
      return
    }

    const card = link.closest('[data-product-card]')
    const list = link.closest('[data-product-list]')

    if (!(card instanceof HTMLElement) || !(list instanceof HTMLElement)) {
      return
    }

    const cards = Array.from(list.querySelectorAll('[data-product-card]'))
    const index = cards.indexOf(card)
    const product = this.getProductCardData(card, index >= 0 ? index : null)

    if (!product) {
      return
    }

    window.dispatchEvent(
      new CustomEvent('product.selected', {
        detail: {
          product: {
            ...product,
            list: list.dataset.productList,
          },
        },
      }),
    )
  }

  getProductCardData = (card, index = null) => {
    const product = {
      sku: card.dataset.productSku,
      name: card.dataset.productName,
      brand: card.dataset.productBrand,
      categories: this.parseItemCategories(card.dataset.ga4Categories),
    }

    if (!product.sku && !product.name) {
      return null
    }

    if (Number.isInteger(index)) {
      product.index = index
    }

    return product
  }

  pushProductList = (list, products, { page = null } = {}) => {
    if (!list || !Array.isArray(products) || products.length === 0) {
      return
    }

    const items = products
      .filter((product) => product?.sku || product?.name)
      .map((product, index) => {
        const item = {
          index: Number.isInteger(product.index) ? product.index : index,
        }

        if (product.sku) {
          item.item_id = product.sku
        }

        if (product.name) {
          item.item_name = product.name
        }

        if (product.brand) {
          item.item_brand = product.brand
        }

        return { ...item, ...product.categories }
      })

    if (items.length === 0) {
      return
    }

    const data = {
      event: 'view_item_list',
      ecommerce: {
        item_list_name: list,
        items,
      },
    }

    if (Number.isInteger(page) && page > 0) {
      data.list_page = page
    }

    this.push({ ecommerce: null })
    this.push(data)
  }

  /**
   * The GA4 item_category to item_category5 parameters (the root category, then its subcategories) that the server
   * renders as JSON in a data-ga4-categories attribute, see ItemCategories.php.
   */
  parseItemCategories = (json) => {
    if (!json) {
      return {}
    }

    try {
      return this.pickItemCategories(JSON.parse(json))
    } catch (error) {
      logger.error('[DataLayer] Failed to parse item categories', error)

      return {}
    }
  }

  pickItemCategories = (item) =>
    Object.fromEntries(Object.entries(item ?? {}).filter(([key, value]) => /^item_category\d?$/.test(key) && typeof value === 'string'))

  /** The value of a line: its price, without VAT like the item prices, times the quantity. */
  value = (price, quantity) => Math.round(price * quantity * 100) / 100

  pushEcommerce = (event, ecommerce) => {
    this.push({ ecommerce: null })
    this.push({
      event,
      ecommerce,
    })
  }

  /**
   * Google Ads enhanced conversions: the visitor's e-mail address and phone number, as JSON rendered by the server
   * (EnhancedConversionData::forLead()), pushed just before the lead event so the Google Ads tag on it finds them in
   * {{Enhanced Conversions}}. Google hashes them in the browser.
   *
   * @see https://support.google.com/google-ads/answer/9888145 Set up enhanced conversions for web
   */
  pushEnhancedConversionData = (json) => {
    const data = json ? JSON.parse(json) : null

    if (!data || Object.keys(data).length === 0) {
      return
    }

    this.push({ enhanced_conversion_data: data })
  }

  push = (data) => {
    window.dataLayer.push(data)

    logger.debug('[DataLayer]', data)
  }
})()
